// Import ("Switch from …") data for the Admin: API calls and TanStack Query options.
// Every response is parsed with the shared @enp/domain/api schema (lib/api.ts). Connector
// names always come from the API (D-012); nothing here names a source system.

import {
  IMPORT_PAGE_MAX,
  IMPORT_RUN_BUSY_STATUSES,
  type ImportDecisionsRequest,
  type ImportReviewItem,
  type ImportRulesUpdateRequest,
  type ImportRunDetail,
  type ImportRunStatus,
  importConnectionListResponseSchema,
  importConnectionSchema,
  importConnectorListResponseSchema,
  importOutcomeListResponseSchema,
  importReviewItemListResponseSchema,
  importRulesResponseSchema,
  importRunDetailSchema,
  importRunListResponseSchema,
  importSourceCategoryListResponseSchema,
} from '@enp/domain/api';
import type { CheckStatus } from '@enp/domain/checks';
import { formatCount } from '@enp/domain/format';
import { OUTCOME_KINDS, type OutcomeKind, type ReviewKind } from '@enp/domain/migration';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  type QueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';

/** How often a busy run is polled. */
export const POLL_MS = 1_000;

export const importKeys = {
  all: ['imports'] as const,
  connectors: () => ['imports', 'connectors'] as const,
  connections: () => ['imports', 'connections'] as const,
  runs: () => ['imports', 'runs'] as const,
  run: (id: string) => ['imports', 'run', id] as const,
  reviewItems: (id: string, kind: ReviewKind | undefined) =>
    ['imports', 'run', id, 'review', kind ?? 'all'] as const,
  outcomes: (id: string, filter: OutcomeFilter) =>
    ['imports', 'run', id, 'outcomes', filter] as const,
  outcomesByRef: (id: string, refs: string[]) => ['imports', 'run', id, 'refs', refs] as const,
  itemSearch: (id: string, q: string) => ['imports', 'run', id, 'item-search', q] as const,
  categories: (id: string) => ['imports', 'run', id, 'categories'] as const,
  rules: () => ['imports', 'rules'] as const,
};

/** A job is working on the run: poll it. */
export function isBusy(status: ImportRunStatus): boolean {
  return (IMPORT_RUN_BUSY_STATUSES as readonly string[]).includes(status);
}

export const connectorsQuery = () =>
  queryOptions({
    queryKey: importKeys.connectors(),
    queryFn: ({ signal }) =>
      api('/imports/connectors', { signal, schema: importConnectorListResponseSchema }),
    staleTime: 5 * 60_000,
    retry: false,
  });

export const connectionsQuery = () =>
  queryOptions({
    queryKey: importKeys.connections(),
    queryFn: ({ signal }) =>
      api('/imports/connections', { signal, schema: importConnectionListResponseSchema }),
    retry: false,
  });

/** Past and current runs, newest first ("Load more"). */
export const runsQuery = () =>
  infiniteQueryOptions({
    queryKey: importKeys.runs(),
    queryFn: ({ pageParam, signal }) =>
      api(`/imports/runs?limit=20${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`, {
        signal,
        schema: importRunListResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    // A list with a busy run keeps itself fresh.
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.items.some((r) => isBusy(r.status)))
        ? POLL_MS * 2
        : false,
    retry: false,
  });

/** One run; polled while a job works on it. */
export const runQuery = (id: string) =>
  queryOptions({
    queryKey: importKeys.run(id),
    queryFn: ({ signal }) =>
      api(`/imports/runs/${encodeURIComponent(id)}`, { signal, schema: importRunDetailSchema }),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && isBusy(status) ? POLL_MS : false;
    },
    retry: false,
  });

/** Every review item of a kind (all pages: a kind can hold more than one page). */
export async function fetchAllReviewItems(
  runId: string,
  kind: ReviewKind | undefined,
  signal?: AbortSignal,
): Promise<{ items: ImportReviewItem[]; total: number }> {
  const items: ImportReviewItem[] = [];
  let cursor: string | null = null;
  let total: number;
  do {
    const params = new URLSearchParams({ limit: String(IMPORT_PAGE_MAX) });
    if (kind) params.set('kind', kind);
    if (cursor) params.set('cursor', cursor);
    const page = await api(`/imports/runs/${encodeURIComponent(runId)}/review-items?${params}`, {
      signal,
      schema: importReviewItemListResponseSchema,
    });
    items.push(...page.items);
    total = page.total;
    cursor = page.next_cursor;
  } while (cursor);
  return { items, total };
}

/** The review queue of a run (every kind, every page). */
export const reviewItemsQuery = (runId: string) =>
  queryOptions({
    queryKey: importKeys.reviewItems(runId, undefined),
    queryFn: ({ signal }) => fetchAllReviewItems(runId, undefined, signal),
    retry: false,
  });

/** Drill-down filter: which source records to list. */
export interface OutcomeFilter {
  outcome?: OutcomeKind;
  kind?: 'item' | 'variant';
  /** Name or SKU contains this. */
  q?: string;
}

/** Source records and what happened to each, one page at a time. */
export const outcomesQuery = (runId: string, filter: OutcomeFilter) =>
  infiniteQueryOptions({
    queryKey: importKeys.outcomes(runId, filter),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: '100' });
      if (filter.outcome) params.set('outcome', filter.outcome);
      if (filter.kind) params.set('record_kind', filter.kind);
      if (filter.q) params.set('q', filter.q);
      if (pageParam) params.set('cursor', pageParam);
      return api(`/imports/runs/${encodeURIComponent(runId)}/outcomes?${params}`, {
        signal,
        schema: importOutcomeListResponseSchema,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

/** Names of a few source records (e.g. the products whose cost doesn't roll up). */
export const outcomesByRefQuery = (runId: string, refs: string[]) =>
  queryOptions({
    queryKey: importKeys.outcomesByRef(runId, refs),
    queryFn: ({ signal }) =>
      api(
        `/imports/runs/${encodeURIComponent(runId)}/outcomes?limit=${IMPORT_PAGE_MAX}&source_refs=${encodeURIComponent(refs.join(','))}`,
        { signal, schema: importOutcomeListResponseSchema },
      ),
    enabled: refs.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });

/** Items of a run whose name or SKU contains `q` (the "another item…" picker). */
export const itemSearchQuery = (runId: string, q: string) =>
  queryOptions({
    queryKey: importKeys.itemSearch(runId, q),
    queryFn: ({ signal }) =>
      api(
        `/imports/runs/${encodeURIComponent(runId)}/outcomes?record_kind=item&limit=50&q=${encodeURIComponent(q)}`,
        { signal, schema: importOutcomeListResponseSchema },
      ),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });

export const categoriesQuery = (runId: string) =>
  queryOptions({
    queryKey: importKeys.categories(runId),
    queryFn: ({ signal }) =>
      api(`/imports/runs/${encodeURIComponent(runId)}/categories`, {
        signal,
        schema: importSourceCategoryListResponseSchema,
      }),
    staleTime: 5 * 60_000,
    retry: false,
  });

export const rulesQuery = () =>
  queryOptions({
    queryKey: importKeys.rules(),
    queryFn: ({ signal }) => api('/imports/rules', { signal, schema: importRulesResponseSchema }),
    retry: false,
  });

// ---------------------------------------------------------------- commands

export const createConnection = (body: { connector_kind: string; credential_ref: string }) =>
  api('/imports/connections', { method: 'POST', body, schema: importConnectionSchema });

export const identifyConnection = (id: string) =>
  api(`/imports/connections/${encodeURIComponent(id)}/identify`, {
    method: 'POST',
    schema: importConnectionSchema,
  });

export const confirmTarget = (id: string, targetIdentity: string) =>
  api(`/imports/connections/${encodeURIComponent(id)}/confirm-target`, {
    method: 'POST',
    body: { target_identity: targetIdentity },
    schema: importConnectionSchema,
  });

export const startRun = (connectionId: string) =>
  api('/imports/runs', {
    method: 'POST',
    body: { connection_id: connectionId },
    schema: importRunDetailSchema,
  });

export const cancelRun = (id: string) =>
  api(`/imports/runs/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    schema: importRunDetailSchema,
  });

export const replanRun = (id: string) =>
  api(`/imports/runs/${encodeURIComponent(id)}/replan`, {
    method: 'POST',
    schema: importRunDetailSchema,
  });

/** Decisions go in batches of at most this many (the API takes up to 5 000). */
export const DECISION_BATCH = 500;

/** How long a set of decision batches may take, waits for re-planning included (B-023). */
export const DECISIONS_TIME_CAP_MS = 10 * 60_000;

/**
 * Saving decisions stopped part-way (B-023): timed out, stopped by the person, or a batch
 * failed. `saved` of `total` reached the server (whole batches, in order); the rest didn't.
 */
export class DecisionBatchError extends Error {
  constructor(
    readonly saved: number,
    readonly total: number,
    readonly reason: 'timeout' | 'aborted' | 'failed',
    override readonly cause?: unknown,
  ) {
    super(
      `${formatCount(saved)} of ${formatCount(total)} decisions saved; the rest weren’t${
        reason === 'timeout'
          ? ' (checking the import again took too long)'
          : reason === 'aborted'
            ? ' (stopped)'
            : ''
      }.`,
    );
    this.name = 'DecisionBatchError';
  }
}

export interface PostDecisionsOptions {
  /** Stops before the next batch (a batch already sent is saved). */
  signal?: AbortSignal;
  /** Default {@link DECISIONS_TIME_CAP_MS}. */
  timeCapMs?: number;
  /** After each batch: how many are saved so far. */
  onProgress?: (saved: number, total: number) => void;
  now?: () => number;
}

/**
 * Posts decisions in batches; the run re-plans after each (answers the last detail). Waits
 * for re-planning between batches, but never longer than the time cap, and stops when
 * `signal` aborts: then it throws {@link DecisionBatchError} saying how many were saved.
 */
export async function postDecisions(
  runId: string,
  decisions: ImportDecisionsRequest['decisions'],
  options: PostDecisionsOptions = {},
): Promise<ImportRunDetail> {
  const now = options.now ?? (() => Date.now());
  const deadline = now() + (options.timeCapMs ?? DECISIONS_TIME_CAP_MS);
  const total = decisions.length;
  let last: ImportRunDetail | undefined;
  let saved = 0;
  const stop = () => {
    if (options.signal?.aborted) throw new DecisionBatchError(saved, total, 'aborted');
    if (now() > deadline) throw new DecisionBatchError(saved, total, 'timeout');
  };
  for (let i = 0; i < decisions.length; i += DECISION_BATCH) {
    stop();
    if (last && isBusy(last.status)) {
      await waitWhileBusy(runId, stop);
      stop();
    }
    const batch = decisions.slice(i, i + DECISION_BATCH);
    try {
      last = await api(`/imports/runs/${encodeURIComponent(runId)}/decisions`, {
        method: 'POST',
        body: { decisions: batch },
        schema: importRunDetailSchema,
      });
    } catch (error) {
      // The first batch failing is a plain failure; a later one says what was saved.
      if (saved === 0) throw error;
      throw new DecisionBatchError(saved, total, 'failed', error);
    }
    saved += batch.length;
    options.onProgress?.(saved, total);
  }
  if (!last) throw new Error('No decisions to post');
  return last;
}

async function waitWhileBusy(runId: string, stop: () => void): Promise<ImportRunDetail> {
  for (;;) {
    const run = await api(`/imports/runs/${encodeURIComponent(runId)}`, {
      schema: importRunDetailSchema,
    });
    if (!isBusy(run.status)) return run;
    stop();
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
}

/**
 * Asks to load a ready run. A report that isn't a full pass must be acknowledged: the
 * status the person saw goes with the request.
 */
export const loadRun = (id: string, acknowledgeStatus: CheckStatus | null) =>
  api(`/imports/runs/${encodeURIComponent(id)}/load`, {
    method: 'POST',
    body: acknowledgeStatus === null ? {} : { acknowledge_status: acknowledgeStatus },
    schema: importRunDetailSchema,
  });

export const saveRules = (body: ImportRulesUpdateRequest) =>
  api('/imports/rules', { method: 'PUT', body, schema: importRulesResponseSchema });

/** Refreshes everything about a run after a change (decisions change the rules too). */
export function invalidateRun(queryClient: QueryClient, runId: string): Promise<void> {
  return queryClient
    .invalidateQueries({ queryKey: ['imports', 'run', runId] })
    .then(() => queryClient.invalidateQueries({ queryKey: importKeys.runs() }))
    .then(() => queryClient.invalidateQueries({ queryKey: importKeys.rules() }));
}

export const archiveConnection = (id: string) =>
  api(`/imports/connections/${encodeURIComponent(id)}/archive`, { method: 'POST' });

/** Search params of the records drill-down. */
export const recordsSearchSchema = z.object({
  // The domain's own list, so a new outcome kind can't be missed here (B-025).
  outcome: z.enum(OUTCOME_KINDS).optional().catch(undefined),
  kind: z.enum(['item', 'variant']).optional().catch(undefined),
  /** Name or SKU contains this. */
  q: z.string().trim().min(1).max(100).optional().catch(undefined),
  /** Records a re-run changed (from the report's "Changes since the last load"). */
  change: z.enum(['added', 'changed', 'removed']).optional().catch(undefined),
  /** Which section of the changes `change` is about. */
  section: z.enum(['products', 'outcomes']).optional().catch(undefined),
  /** `missing`: the products without a standard cost (from the report's cost roll-up). */
  cost: z.enum(['missing']).optional().catch(undefined),
});
